import React from 'react';
import { getTpmsTireColor, TireStatus } from 'utils/tpms';
import TireTooltip from 'components/Tire/TireTooltip';
import BatteryIndicator from 'components/Tire/BatteryIndicator';
import type { TireInfo } from 'components/Tire/TireTooltip';

interface TireAlert {
  icon: string;
  color: string;
}

interface TireData {
  p1?: TireInfo;
  p2?: TireInfo;
  p3?: TireInfo;
  p4?: TireInfo;
  p5?: TireInfo;
  p6?: TireInfo;
}

interface ThreeAxlesSixWheelsProps {
  values: {
    p1Status?: TireStatus;
    p2Status?: TireStatus;
    p3Status?: TireStatus;
    p4Status?: TireStatus;
    p5Status?: TireStatus;
    p6Status?: TireStatus;
    p1?: number;
    p2?: number;
    p3?: number;
    p4?: number;
    p5?: number;
    p6?: number;
    p1HasSensor?: boolean;
    p2HasSensor?: boolean;
    p3HasSensor?: boolean;
    p4HasSensor?: boolean;
    p5HasSensor?: boolean;
    p6HasSensor?: boolean;
    isDataStale?: boolean;
    p1IsStale?: boolean;
    p2IsStale?: boolean;
    p3IsStale?: boolean;
    p4IsStale?: boolean;
    p5IsStale?: boolean;
    p6IsStale?: boolean;
  };
  tireData?: TireData;
  batteryLow?: {
    p1?: boolean;
    p2?: boolean;
    p3?: boolean;
    p4?: boolean;
    p5?: boolean;
    p6?: boolean;
  };
  width?: number | string;
  height?: number | string;
  tireStatusMap?: {
    p1?: TireStatus;
    p2?: TireStatus;
    p3?: TireStatus;
    p4?: TireStatus;
    p5?: TireStatus;
    p6?: TireStatus;
  };
  tireStaleMap?: Record<string, boolean>;
  tireAlertMap?: Record<string, TireAlert>;
}

const ThreeAxlesSixWheels: React.FC<ThreeAxlesSixWheelsProps> = ({
  values,
  tireData,
  batteryLow,
  width = 65,
  height = 120,
  tireStatusMap,
  tireStaleMap,
  tireAlertMap,
}) => {
  const {
    p1Status,
    p2Status,
    p3Status,
    p4Status,
    p5Status,
    p6Status,
    isDataStale,
    p1IsStale,
    p2IsStale,
    p3IsStale,
    p4IsStale,
    p5IsStale,
    p6IsStale,
  } = values;

  const isNumber = (value: any) => typeof value === 'number';

  const renderTireAlert = (key: string, x: number, y: number) => {
    const alert = tireAlertMap?.[key];
    if (!alert) return null;

    // Shrink play symbols (leakage) so they fit inside the tire circle
    const isPlayIcon = alert.icon.includes('▶');
    const alertFontSize = isPlayIcon ? (alert.icon.length > 1 ? 5 : 7) : 12;

    return (
      <g style={{ pointerEvents: 'none' }}>
        <circle
          cx={x}
          cy={y}
          r="6"
          fill={alert.color}
          stroke="#FFFFFF"
          strokeWidth="1.4"
        />
        <text
          x={x}
          y={y}
          textAnchor="middle"
          dominantBaseline="middle"
          fill="#FFFFFF"
          fontSize={alertFontSize}
          fontWeight="900"
        >
          {alert.icon}
        </text>
      </g>
    );
  };

  return (
    <svg
      width={width}
      height={height}
      viewBox="0 0 65 120"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
    >
      {/* Front axle */}
      <line
        x1="15"
        y1="12.5"
        x2="50"
        y2="12.5"
        stroke="#4A5C72"
        strokeWidth="5"
      />

      {/* Middle axle */}
      <line
        x1="5"
        y1="57.5"
        x2="60"
        y2="57.5"
        stroke="#4A5C72"
        strokeWidth="5"
      />

      {/* Rear axle */}
      <line
        x1="5"
        y1="102.5"
        x2="60"
        y2="102.5"
        stroke="#4A5C72"
        strokeWidth="5"
      />

      {/* Center spine */}
      <line
        x1="32.5"
        y1="15"
        x2="32.5"
        y2="105"
        stroke="#4A5C72"
        strokeWidth="5"
      />

      {/* Front Left Wheel (P1) */}
      <TireTooltip tireInfo={tireData?.p1}>
        <g>
          <rect
            x="5"
            y="0"
            width="10"
            height="25"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p1 ?? p1Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p1 ?? p1IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={10}
            cy={12.5}
            tireBottomY={25}
            tireWidth={10}
            show={batteryLow?.p1}
          />
          {renderTireAlert('p1', 10, 12.5)}
        </g>
      </TireTooltip>

      {/* Front Right Wheel (P2) */}
      <TireTooltip tireInfo={tireData?.p2}>
        <g>
          <rect
            x="50"
            y="0"
            width="10"
            height="25"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p2 ?? p2Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p2 ?? p2IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={55}
            cy={12.5}
            tireBottomY={25}
            tireWidth={10}
            show={batteryLow?.p2}
          />
          {renderTireAlert('p2', 55, 12.5)}
        </g>
      </TireTooltip>

      {/* Middle Left Wheel (P3) */}
      <TireTooltip tireInfo={tireData?.p3}>
        <g>
          <rect
            x="5"
            y="45"
            width="10"
            height="25"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p3 ?? p3Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p3 ?? p3IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={10}
            cy={57.5}
            tireBottomY={70}
            tireWidth={10}
            show={batteryLow?.p3}
          />
          {renderTireAlert('p3', 10, 57.5)}
        </g>
      </TireTooltip>

      {/* Middle Right Wheel (P4) */}
      <TireTooltip tireInfo={tireData?.p4}>
        <g>
          <rect
            x="50"
            y="45"
            width="10"
            height="25"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p4 ?? p4Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p4 ?? p4IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={55}
            cy={57.5}
            tireBottomY={70}
            tireWidth={10}
            show={batteryLow?.p4}
          />
          {renderTireAlert('p4', 55, 57.5)}
        </g>
      </TireTooltip>

      {/* Rear Left Wheel (P5) */}
      <TireTooltip tireInfo={tireData?.p5}>
        <g>
          <rect
            x="5"
            y="90"
            width="10"
            height="25"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p5 ?? p5Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p5 ?? p5IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={10}
            cy={102.5}
            tireBottomY={115}
            tireWidth={10}
            show={batteryLow?.p5}
          />
          {renderTireAlert('p5', 10, 102.5)}
        </g>
      </TireTooltip>

      {/* Rear Right Wheel (P6) */}
      <TireTooltip tireInfo={tireData?.p6}>
        <g>
          <rect
            x="50"
            y="90"
            width="10"
            height="25"
            rx="1.90055"
            fill={getTpmsTireColor(
              tireStatusMap?.p6 ?? p6Status ?? TireStatus.NO_SENSOR,
              tireStaleMap?.p6 ?? p6IsStale ?? isDataStale
            )}
          />
          <BatteryIndicator
            cx={55}
            cy={102.5}
            tireBottomY={115}
            tireWidth={10}
            show={batteryLow?.p6}
          />
          {renderTireAlert('p6', 55, 102.5)}
        </g>
      </TireTooltip>
    </svg>
  );
};

export default ThreeAxlesSixWheels;
